import * as React from 'react';
import { useState, useEffect } from 'react';

/** Material UI Packages. */
import { Box, Typography } from '@mui/material';
import { DataGridPremium } from '@mui/x-data-grid-premium';

/** Redux Packages. */
import { useDispatch, useSelector } from 'react-redux';

/** Auth0 authenticator Packages. */
import { useAuth0 } from '@auth0/auth0-react';

/** Redux Reducers. */
import { setSnackbar } from '../../state/slices/snackBarSlice';

/** Internal Components. */
import { AutocompleteFieldObject } from '../../components/AutoComplete';
import ThemeButton from '../../components/Button';
import CircularLoading from '../../components/CircularLoading';
import { callAPI } from '../../api/useApiRequest';

import { TPRowsCol } from './TableDef';

export default function TPSharing(props) {
    /** Get props from Account.js. */
    const { acctRows, setAcctRows, selectedId } = props;
    /** Retrieve authentication details from Auth0. */
    const { user, getAccessTokenSilently } = useAuth0();

    /** Get the dispatch function from Redux to send actions to the store. */
    const dispatch = useDispatch();
    /** Get the trade partner list from the store. */
    const tradePartnerList = useSelector((state) => state.tradePartners);
    /** State to display the assigned trade partners. */
    const [tpRows, setTpRows] = useState([]);
    /** State to display the selected trade partner through the autocomplete. */
    const [selectedTP, setSelectedTP] = useState({});
    const [loading, setLoading] = useState({ delete: false, post: false });

    /** Handle to delete a trade partner. */
    const handleDeleteTP = (event, value) => {
        if (window.confirm('Are you sure you want to delete this trade partner?')) {
            callAPI({
                method: 'delete',
                module: 'customer_mapping',
                component: 'customer_trade_partners',
                getAccessTokenSilently,
                email: user.email,
                obj: JSON.stringify({ id: value.il_customer_tradepartner_map_id }),
                onInit: () => {
                    setLoading({ ...loading, delete: true });
                },
                onSuccess: (resp) => {
                    setTpRows(tpRows.filter((item) => item.id !== value.id));
                    handleUpdateAcctRows('delete', value.il_customer_tradepartner_map_id);
                    dispatch(
                        setSnackbar({
                            open: true,
                            message: 'Trade Partner has been successfully deleted.',
                            severity: 'success',
                        }),
                    );
                },
                onError: () => {
                    dispatch(
                        setSnackbar({
                            open: true,
                            message: 'Something went wrong. Please try again.',
                            severity: 'error',
                        }),
                    );
                },
                onFinal: () => {
                    setLoading({ ...loading, delete: false });
                },
            });
        }
    };

    /** Handle to change a trade partner. */
    const handleChangeTP = (event, value) => {
        setSelectedTP(value);
    };

    /** Handle to add a trade partner. */
    const handleAddTP = () => {
        const isExisting = tpRows.some((tp) => tp.id === selectedTP.id);
        const payload = {
            il_customer_id: selectedId,
            il_trade_partner_id: selectedTP.id,
        };

        if (!payload.il_trade_partner_id) {
            dispatch(
                setSnackbar({
                    open: true,
                    message: 'Trade Partner is not selected.',
                    severity: 'warning',
                }),
            );
            return;
        }

        if (isExisting) {
            dispatch(
                setSnackbar({
                    open: true,
                    message: 'This Trade Partner is already added.',
                    severity: 'warning',
                }),
            );
            return;
        }

        callAPI({
            method: 'post',
            module: 'customer_mapping',
            component: 'customer_trade_partners',
            getAccessTokenSilently,
            email: user.email,
            obj: JSON.stringify(payload),
            onInit: () => {
                setLoading({ ...loading, post: true });
            },
            onSuccess: (resp) => {
                setTpRows([...tpRows, { ...selectedTP, il_customer_tradepartner_map_id: resp.data.id }]);
                setSelectedTP({});
                handleUpdateAcctRows('post', resp.data);
                dispatch(
                    setSnackbar({
                        open: true,
                        message: 'Trade Partner has been successfully added.',
                        severity: 'success',
                    }),
                );
            },
            onError: (resp) => {
                dispatch(
                    setSnackbar({ open: true, message: 'Something went wrong. Please try again.', severity: 'error' }),
                );
            },
            onFinal: () => {
                setLoading({ ...loading, post: false });
            },
        });
    };

    const handleUpdateAcctRows = (method, value) => {
        if (method === 'post') {
            /** e.g. value = {id: 1, il_trade_partner_id: 1, il_customer_id: 1} */
            setAcctRows(
                acctRows.map((item) =>
                    item.id === selectedId
                        ? { ...item, il_customer_tradepartner_map: [...item.il_customer_tradepartner_map, value] }
                        : item,
                ),
            );
        } else {
            /** e.g. value = 1 */
            setAcctRows(
                acctRows.map((item) =>
                    item.id === selectedId
                        ? {
                              ...item,
                              il_customer_tradepartner_map: item.il_customer_tradepartner_map.filter(
                                  (item) => item.id !== value,
                              ),
                          }
                        : item,
                ),
            );
        }
    };

    /** Get the trade partner list from the selected account. */
    useEffect(() => {
        const selectedAccount = acctRows.find((item) => item.id === selectedId);
        if (!selectedAccount) return;

        const tradePartnerIds = selectedAccount.il_customer_tradepartner_map.map((item) => item.il_trade_partner_id);
        const matchedTradePartners = tradePartnerList
            .map((tp) => {
                // il_customer_tradepartner_map에서 이 trade partner에 해당하는 매핑 찾기
                const mapItem = selectedAccount.il_customer_tradepartner_map.find(
                    (map) => map.il_trade_partner_id === tp.id,
                );

                if (!mapItem) return null; // 매핑이 없으면 제외
                return {
                    ...tp,
                    il_customer_tradepartner_map_id: mapItem.id, // 새 키 추가
                };
            })
            .filter(Boolean); // null 제거
        setTpRows(matchedTradePartners);
    }, [tradePartnerList]);

    if (loading.post || loading.delete) {
        return <CircularLoading loading={loading.post || loading.delete} />;
    }

    return (
        <Box sx={{ my: 2, mx: 5, height: '95%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                    sx={{
                        width: '3px',
                        height: '16px',
                        backgroundColor: 'primary.main',
                    }}
                />
                <Typography sx={{ fontWeight: 500, fontSize: 16, color: 'text.secondary' }}>
                    Trade Partner Sharing
                </Typography>
            </Box>
            <Box sx={{ my: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
                <AutocompleteFieldObject
                    options={tradePartnerList}
                    label="TP Sharing"
                    value={selectedTP}
                    onChange={handleChangeTP}
                    labelStyle={{ width: '100x', gap: '0px' }}
                    AutocompleteStyle={{ width: '300px' }}
                    disable={true}
                />
                <ThemeButton text="Add" onClick={handleAddTP} />
            </Box>
            <DataGridPremium
                rows={tpRows}
                columns={TPRowsCol(handleDeleteTP)}
                density="compact"
                disableRowSelectionOnClick
                unstable_cellSelection
                disableColumnMenu
                disableColumnSorting
                initialState={{
                    columns: {
                        columnVisibilityModel: {
                            id: false,
                        },
                    },
                }}
                sx={{ width: '800px', height: '85%' }}
            />
        </Box>
    );
}
